/**
 * ProjectService — creates, opens, renames and deletes projects.
 */

import type { BucketRepository, ProjectRepository } from '../repositories';
import type { Project } from '../types';
import type { ChangeSink, Clock } from './ports';

export class ProjectService {
  constructor(
    private readonly projects: ProjectRepository,
    private readonly buckets: BucketRepository,
    private readonly sink: ChangeSink,
    private readonly clock: Clock,
  ) {}

  list(): Project[] {
    return this.projects.list();
  }

  get(id: number): Project | null {
    return this.projects.byId(id);
  }

  /** A new project always arrives with its two buckets already in place. */
  create(name: string, previewTargetPx: number): Project {
    const trimmed = name.trim() || 'Untitled shoot';
    const id = this.projects.create(trimmed, previewTargetPx, this.clock());
    this.buckets.createDefaults(id);
    this.sink.touch();
    return this.projects.byId(id)!;
  }

  open(id: number): Project | null {
    const project = this.projects.byId(id);
    if (!project) return null;

    this.projects.touchOpened(id, this.clock());
    this.sink.touch();
    return this.projects.byId(id);
  }

  rename(id: number, name: string): Project | null {
    const trimmed = name.trim();
    if (!trimmed) return this.projects.byId(id);

    this.projects.rename(id, trimmed, this.clock());
    this.sink.touch();
    return this.projects.byId(id);
  }

  setAutoAdvance(id: number, value: boolean): void {
    this.projects.setAutoAdvance(id, value, this.clock());
    this.sink.touch();
  }

  setLastViewed(id: number, mediaId: number | null): void {
    this.projects.setLastViewed(id, mediaId);
    this.sink.touch();
  }

  delete(id: number): void {
    this.projects.delete(id);
    this.sink.touch();
  }
}
